import i18n from '../../../utils/translations/i18n';
import Styles from './index.module.scss';
import TextBox from "../../shared/elements/textbox";
import Button from "../../shared/elements/button";
import { useAuthFrontLock } from '../../../context/useAuthFrontContext';
import { useState, useEffect } from 'react';
import ErrorBar from '../../shared/elements/errorBar';
import { useLoader } from '../../../context/useLoader';
import validateEmail from '../../../utils/validations/emailValidation';
import { getEmailOTPCounterStatus } from "../../../utils/services/services";
import { ERROR_CODE, EXCLUDES_MESSAGES, SUCCESS_MESSAGES } from '../../../utils/constant/constant';

const SendOTPForm = ({ form, setFormState, setShowVerifyPage, sendOTPHandler, isSignupForm }) => {
    const { t } = i18n;
    const { showLoader, hideLoader } = useLoader();
    const [errorBarState, setErrorBarState] = useState({isActive:false, message:''});
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);

    const authFrontLock = useAuthFrontLock();

    const errorMessages = {
        [ERROR_CODE.GENERIC_ERROR]: t('email_otp.generic_error'),
        [ERROR_CODE.OTP_MAX_ATTEMPT_ERROR]: t('email_otp.max_attempt_reached_error'),
    };

    useEffect(() => {
        const { error, errorId} = authFrontLock.getAdditionalParams();
        const errorBarId = window.localStorage.getItem('errorBarId');

        if (error && errorId && errorBarId !== errorId.toString() && !SUCCESS_MESSAGES.includes(error) && !EXCLUDES_MESSAGES.includes(error)) {
            window.localStorage.setItem('errorBarId', errorId);
            showErrorMessage(ERROR_CODE.GENERIC_ERROR);

            const loginHint = authFrontLock.getLoginHint();
            if (loginHint) {
                setFormState(prevState => ({
                    ...prevState,
                    email: {
                        ...prevState.email,
                        value: loginHint
                    }
                }));
            }
        }
    }, []);

    const showErrorMessage = (error) => {
        if (errorMessages[error]) {
            setErrorBarState({ isActive: true, message: errorMessages[error] });
        } else {
            setErrorBarState({ isActive: true, message: errorMessages[ERROR_CODE.GENERIC_ERROR] });
        }
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormState(prevForm => ({
            ...prevForm,
            [name]: {
                ...prevForm[name],
                value,
                dangerWarn: false,
                errorMessage: null
            }
        }));
        setIsButtonDisabled(false);
    };

    const handleBlur = (event) => {
        const fieldName = event.target.name;
        const { regex, value } = form[fieldName];
        const expression = new RegExp(regex);
        const showRegexError = value != '' && !expression.test(value);
        setFormState(prevForm => ({
            ...prevForm,
            [fieldName]: {
                ...prevForm[fieldName],
                dangerWarn: showRegexError,
                errorMessage: showRegexError ? t('email_otp.invalid_email_error') : null
            }
        }));
    }


    const validateForm = () => {
        const formObj = {...form};
        let regexError = false, nullError = false;
        
        formObj.formFields.forEach(field => {
            const fieldObj = formObj[field], {regex, value, required} = fieldObj, expression = new RegExp(regex);
            formObj[field].dangerWarn = false;
            formObj[field].errorMessage = '';

            if (value !== '' && !expression.test(value)) {
                regexError = true;
                formObj[field].dangerWarn = true;
                formObj[field].errorMessage = t('email_otp.invalid_email_error');

            }
            if (value === '' && required) {
                nullError = true;
                formObj[field].dangerWarn = true;
                formObj[field].errorMessage = t('email_otp.email_required_error');
            }
        });

        setFormState(formObj);
        return !(regexError || nullError);
    };


    const sendOtp = async () => {
        try {
            await sendOTPHandler();
            setShowVerifyPage(true);
        } catch (err: any){
            showErrorMessage(ERROR_CODE.GENERIC_ERROR);
        }
    }

    const handleSubmit = async (event) => {
        try {
            showLoader();
            event.preventDefault();
            setErrorBarState({isActive: false, message: ''});
            if (validateForm()) {
                const [emailValidationResult, otpCounterStatus] = await Promise.all([
                    validateEmail(form.email.value),
                    getEmailOTPCounterStatus(form.email.value)
                ]);
    
                if (emailValidationResult.isValid && otpCounterStatus.isEligible) {
                    await sendOtp();
                } else if (!emailValidationResult.isValid) {
                    setFormState(prevForm => ({
                        ...prevForm,
                        email: {
                            ...prevForm.email,
                            dangerWarn: true,
                            errorMessage: t('email_otp.invalid_email_error'),
                        }
                    }));
                } else {
                    setIsButtonDisabled(true);
                    showErrorMessage(ERROR_CODE.OTP_MAX_ATTEMPT_ERROR);
                }
            }
        } catch (err: any) {
            showErrorMessage(ERROR_CODE.GENERIC_ERROR);
        } finally {
            hideLoader();
        }
    }
    
    return (
        <>
            {errorBarState.isActive && <ErrorBar message={errorBarState.message} />}
            <form className={Styles.form}>
                <div>{t('email_otp.enter_email_text')}</div>
                {form.formFields.map(field => {
                    return (<TextBox
                        key={form[field].id}
                        id={form[field].id}
                        className={form[field].className}
                        name={form[field].name}
                        type={form[field].type}
                        max={form[field].maxLength}
                        label={form[field].label}
                        placeholder={form[field].label}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isRequired={form[field].required}
                        value={form[field].value}
                        dangerWarn={form[field].dangerWarn}
                        errorMessage={form[field].errorMessage}
                    ></TextBox>)
                })}
                <Button id="send-otp-button" type="submit" onClick={handleSubmit} subClass="btn--pill" className={Styles.submitButton} isDisabled={isButtonDisabled}>
                    {t('email_otp.send_verification_link')}
                </Button>
            </form>
        </>
    )
}

export default SendOTPForm;
